import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Button, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useFormikContext } from 'formik';

import { ContentInput } from 'api';
import { Loaders } from 'components';
import { useFeatures, useFollowed } from 'hooks';
import { getPostableGroupsToApprove } from 'features/content/helpers';

import { PostSchema } from '../../validations';
import { usePostContext } from '../../PostContext';

const useStyles = makeStyles(theme => ({
  button: {
    marginLeft: theme.spacing(2),
    minWidth: 100,
  },
}));

const FormButtons: FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { state } = usePostContext();
  const { postable } = useFollowed();
  const { singleGroupPost } = useFeatures();

  const history = useHistory();
  const { dirty, values, isSubmitting, setFieldValue } = useFormikContext<ContentInput>();
  const groupsToApprove = getPostableGroupsToApprove(values, postable.list);

  const isValid = PostSchema.isValidSync(values, {
    uiState: state,
    singlePostActive: singleGroupPost,
  });

  // Safety measure for feature testing single vs multi post
  if (singleGroupPost && values.primary_group_id) {
    delete values.primary_group_id;
  }

  /**
   * We are coercing group_ids in various places within the GroupDropdown and the SingleGroupDropdown Components.
   * This makes it easier to work in those components especially since they share the same Formik
   * context. For now we must add back the primary group id that we omitted to add in the SingleGroupDropdown
   * context. This ensures a predictable handshake between client/server.
   */
  if (isSubmitting && values.primary_group_id) {
    const hasPrimaryGroupId = values.group_ids.find(group => group === values.primary_group_id);
    !hasPrimaryGroupId &&
      setFieldValue('group_ids', [values.primary_group_id, ...values.group_ids]);
  }

  function getSaveText() {
    if (groupsToApprove.length) {
      return t('content:saveAndApprove');
    }

    return values.content_id ? t('common:save') : t('common:post');
  }

  return (
    <Grid item xs container justify="flex-end">
      <Grid item>
        <Button
          className={classes.button}
          variant="outlined"
          size="large"
          onClick={() => history.goBack()}
        >
          {t('common:cancel')}
        </Button>
      </Grid>
      <Grid item>
        <Button
          className={classes.button}
          color="primary"
          variant="contained"
          size="large"
          disabled={!isValid || !dirty}
          type="submit"
        >
          {isSubmitting ? <Loaders.Standard height={26} /> : getSaveText()}
        </Button>
      </Grid>
    </Grid>
  );
};

export default FormButtons;
